import Link from "next/link";
import { usePathname } from "next/navigation";
import { Home, Landmark, Trophy, User } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

// ============================================================================
// THE MINT Bottom Tab Bar — primary navigation across all screen sizes
// ============================================================================

interface Tab {
  href: string;
  label: string;
  icon: LucideIcon;
}

const TABS: Tab[] = [
  { href: "/", label: "Home", icon: Home },
  { href: "/store", label: "Mint", icon: Landmark },
  { href: "/events", label: "Events", icon: Trophy },
  { href: "/profile", label: "Profile", icon: User },
];

export const BOTTOM_TAB_BAR_PADDING = "pb-[calc(4rem+env(safe-area-inset-bottom))]";

export function isBottomTabBarVisible(pathname: string): boolean {
  return !pathname.startsWith("/login") && !pathname.startsWith("/admin");
}

function isTabActive(pathname: string, href: string): boolean {
  if (href === "/") return pathname === "/";
  return pathname === href || pathname.startsWith(`${href}/`);
}

export function BottomTabBar() {
  const pathname = usePathname();

  if (!isBottomTabBarVisible(pathname)) {
    return null;
  }

  return (
    <nav
      className="fixed bottom-0 inset-x-0 z-40 border-t border-surface-3 bg-surface/95 backdrop-blur-md pb-[env(safe-area-inset-bottom)]"
      aria-label="Primary"
    >
      <div className="mx-auto grid max-w-md grid-cols-4">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const active = isTabActive(pathname, tab.href);
          return (
            <Link
              key={tab.href}
              href={tab.href}
              aria-current={active ? "page" : undefined}
              className={cn(
                "group relative flex flex-col items-center justify-center gap-1 py-2.5",
                "text-[10px] font-serif font-medium tracking-wide transition-colors duration-(--duration-micro)",
                active ? "text-gold" : "text-tertiary hover:text-cream",
              )}
            >
              {active && (
                <span className="absolute top-0 h-0.5 w-8 -translate-y-px rounded-full bg-gold shadow-[0_0_12px_rgba(184,160,76,0.35)]" />
              )}
              <Icon
                className={cn(
                  "h-5 w-5 transition-transform duration-(--duration-micro)",
                  active ? "scale-110" : "group-hover:scale-105",
                )}
                strokeWidth={active ? 2.25 : 1.75}
              />
              <span>{tab.label}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
}
